import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Clock3, Users2 } from "lucide-react";
import { Link, Navigate, useOutletContext, useParams } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PremiumStatusBadge } from "@/components/equinox/PremiumStatusBadge";
import { getServiceDetailBySlug } from "@/components/equinox/serviceDetailsData";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { onPrimaryAction } = useOutletContext();
  const detail = getServiceDetailBySlug(slug);

  if (!detail) {
    return <Navigate to="/services" replace />;
  }

  const Icon = detail.icon;

  return (
    <section className="px-6 pb-20 pt-16 sm:px-8 lg:px-12 lg:pb-24 lg:pt-20" data-testid="service-detail-page">
      <div className="mx-auto max-w-7xl space-y-8">
        <Link
          to="/services"
          className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-4 py-2 text-sm text-white/78 transition-transform duration-300 hover:-translate-y-0.5"
          data-testid="service-detail-back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Services
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="glitter-card glass-panel noise-surface overflow-hidden rounded-[2.5rem] p-6 lg:p-8"
          data-testid="service-detail-hero"
        >
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
            <div>
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-[1.5rem] border border-white/[0.12] bg-white/[0.05] text-blue-100 shadow-[0_0_30px_rgba(37,99,235,0.12)]">
                <Icon className="h-7 w-7" />
              </div>
              <p className="section-kicker mt-6">{detail.eyebrow}</p>
              <h1 className="mt-5 max-w-[12ch] font-display text-4xl tracking-[-0.05em] text-white sm:text-5xl lg:text-6xl" data-testid="service-detail-title">
                {detail.title}
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-8 text-white/[0.66]" data-testid="service-detail-intro">
                {detail.intro}
              </p>
              <div className="mt-6 flex flex-wrap gap-2" data-testid="service-detail-status-strip">
                <PremiumStatusBadge label="Operator-led" variant="live" testId="service-detail-status-operator" />
                <PremiumStatusBadge label="Premium execution layer" variant="benchmark" testId="service-detail-status-premium" />
                <PremiumStatusBadge label="Built for conversion" variant="estimated" testId="service-detail-status-conversion" />
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to={detail.primaryLink.href}
                  className="glass-button glass-panel rounded-full bg-white px-7 py-4 text-sm font-medium text-black transition-all duration-300 hover:scale-[1.03]"
                  data-testid="service-detail-primary-link"
                >
                  <span className="relative z-10 inline-flex items-center gap-2">
                    {detail.primaryLink.label}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
                <Link
                  to={detail.secondaryLink.href}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-7 py-4 text-sm font-medium text-white/82 backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-300/30 hover:bg-blue-400/10 hover:text-white"
                  data-testid="service-detail-secondary-link"
                >
                  {detail.secondaryLink.label}
                </Link>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3" data-testid="service-detail-facts-grid">
              {detail.heroFacts.map((fact, index) => (
                <div key={fact.label} className="control-room-panel rounded-[1.7rem] p-5" data-testid={`service-detail-fact-${index + 1}`}>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.4]">{fact.label}</p>
                  <p className="mt-3 font-display text-3xl text-white">{fact.value}</p>
                  <p className="mt-3 text-sm leading-7 text-white/[0.62]">{fact.helper}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-[1.12fr_0.88fr]">
          <div className="glitter-card glass-panel noise-surface rounded-[2rem] p-6" data-testid="service-detail-process-section">
            <p className="section-kicker">How it gets done</p>
            <h2 className="mt-5 font-display text-3xl text-white">The execution path behind the service.</h2>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {detail.process.map((step, index) => (
                <div key={step.title} className="control-room-panel rounded-[1.5rem] p-5" data-testid={`service-detail-step-${index + 1}`}>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-blue-100">Step {index + 1}</p>
                  <p className="mt-3 font-display text-2xl text-white">{step.title}</p>
                  <p className="mt-4 text-sm leading-7 text-white/[0.64]">{step.body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="glitter-card glass-panel noise-surface rounded-[2rem] p-6" data-testid="service-detail-operating-model-card">
              <div className="flex items-center gap-3 text-blue-100">
                <Clock3 className="h-4 w-4" />
                <p className="text-[11px] uppercase tracking-[0.22em]">Operating model</p>
              </div>
              <div className="mt-5 space-y-4">
                {detail.capabilityCards.map((card, index) => (
                  <div key={card.title} className="control-room-panel rounded-[1.4rem] p-4" data-testid={`service-detail-capability-${index + 1}`}>
                    <p className="font-display text-xl text-white">{card.title}</p>
                    <p className="mt-3 text-sm leading-7 text-white/[0.64]">{card.body}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="glitter-card glass-panel noise-surface rounded-[2rem] p-6" data-testid="service-detail-ideal-fit-card">
              <div className="flex items-center gap-3 text-blue-100">
                <Users2 className="h-4 w-4" />
                <p className="text-[11px] uppercase tracking-[0.22em]">Ideal fit</p>
              </div>
              <div className="mt-5 space-y-3">
                {detail.idealFor.map((item, index) => (
                  <div key={item} className="control-room-panel flex gap-3 rounded-[1.2rem] p-4" data-testid={`service-detail-ideal-fit-${index + 1}`}>
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-100" />
                    <p className="text-sm leading-7 text-white/[0.68]">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="glitter-card glass-panel noise-surface rounded-[2rem] p-6" data-testid="service-detail-deliverables-section">
          <p className="section-kicker">Deliverables</p>
          <h2 className="mt-5 font-display text-3xl text-white">What the team actually ships.</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {detail.deliverables.map((item, index) => (
              <div key={item} className="control-room-panel rounded-[1.4rem] p-5" data-testid={`service-detail-deliverable-${index + 1}`}>
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.4]">Deliverable {String(index + 1).padStart(2, "0")}</p>
                <p className="mt-3 text-sm leading-7 text-white/[0.7]">{item}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="glitter-card glass-panel noise-surface rounded-[2rem] p-6" data-testid="service-detail-faq-section">
          <p className="section-kicker">FAQs</p>
          <h2 className="mt-5 font-display text-3xl text-white">What founders usually ask before they engage.</h2>
          <Accordion type="single" collapsible className="mt-8">
            {detail.faqs.map((faq, index) => (
              <AccordionItem key={faq.question} value={`item-${index + 1}`} className="border-white/[0.08]">
                <AccordionTrigger className="text-base text-white hover:no-underline" data-testid={`service-detail-faq-trigger-${index + 1}`}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-7 text-white/[0.64]" data-testid={`service-detail-faq-content-${index + 1}`}>
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="glitter-card glass-panel noise-surface rounded-[2.2rem] p-6 lg:p-8" data-testid="service-detail-cta-section">
          <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="section-kicker">Next step</p>
              <h2 className="mt-5 max-w-[14ch] font-display text-3xl text-white sm:text-4xl">If this is the lever you need, we can map the right execution path.</h2>
              <p className="mt-5 max-w-2xl text-sm leading-8 text-white/[0.64]">
                We can turn this into a tighter audit, a service plan, or a founder conversation depending on how ready the team is to move.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <button
                type="button"
                onClick={onPrimaryAction}
                className="glass-button glass-panel rounded-full bg-white px-7 py-4 text-sm font-medium text-black transition-all duration-300 hover:scale-[1.03]"
                data-testid="service-detail-book-call-button"
              >
                <span className="relative z-10 inline-flex items-center gap-2">
                  Book a Growth Call
                  <ArrowRight className="h-4 w-4" />
                </span>
              </button>
              <Link
                to={detail.primaryLink.href}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-7 py-4 text-sm font-medium text-white/82 backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-300/30 hover:bg-blue-400/10 hover:text-white"
                data-testid="service-detail-bottom-link"
              >
                {detail.primaryLink.label}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}